import React, { useState, useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, ChevronLeft, ChevronRight, Hash, History, AlertTriangle } from 'lucide-react';
import { ProductFormDialog } from './components/ProductFormDialog';
import { CategoryManager } from './components/CategoryManager';
import { BrandManager } from './components/BrandManager';
import { ReceivingHistoryDialog } from './components/ReceivingHistoryDialog';
import { DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Textarea as TextareaInput } from '../../components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminProducts() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [editingProduct, setEditingProduct] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyProduct, setHistoryProduct] = useState(null);
  const [receivingHistory, setReceivingHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [editingStock, setEditingStock] = useState({});
  const [stockConfirm, setStockConfirm] = useState(null); // { productId, productName, oldQty, newQty }
  const [stockReason, setStockReason] = useState('');
  const [newBrandDialogOpen, setNewBrandDialogOpen] = useState(false);
  const [newCategoryDialogOpen, setNewCategoryDialogOpen] = useState(false);
  const [newBrandName, setNewBrandName] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategorySlug, setNewCategorySlug] = useState('');

  const emptyProduct = {
    name: '', slug: '', sku: '', description: '', brand: '', category_id: '',
    supplier_id: '', images: [], pricing: { retail: 0, trade: 0 }, specs: [],
    stock_qty: 0, low_stock_threshold: 5, min_order_qty: 1, max_order_qty: '',
    supplier_moq: '', reorder_qty: '',
    track_serials: false, allow_backorder: false, is_active: true,
    weight_kg: '', length_cm: '', width_cm: '', height_cm: '',
  };

  const [form, setForm] = useState(emptyProduct);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchBrands();
    fetchSuppliers();
  }, [pagination.page]);

  // Handle search highlight - auto-open product from global search
  useEffect(() => {
    const highlightId = searchParams.get('highlight') || location.state?.highlightId;
    if (!highlightId) return;

    const openProduct = async (id) => {
      // Try current page first
      let product = products.find(p => p.id === id);
      if (!product) {
        // Fetch directly from API (item may be on a different page)
        try {
          const token = localStorage.getItem('token');
          const res = await axios.get(`${API}/admin/products/${id}`, { headers: { Authorization: `Bearer ${token}` } });
          product = res.data;
        } catch { return; }
      }
      if (product) {
        setEditingProduct(product);
        setForm({ ...emptyProduct, ...product });
        setDialogOpen(true);
      }
      setSearchParams({}, { replace: true });
      window.history.replaceState({}, '');
    };

    openProduct(highlightId);
  }, [products, searchParams, location.state]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/products?page=${pagination.page}`);
      setProducts(res.data.products);
      setPagination({ page: res.data.page, pages: res.data.pages, total: res.data.total });
    } catch (err) { console.error('Error fetching products:', err); }
    finally { setLoading(false); }
  };

  const fetchCategories = async () => {
    try { const res = await axios.get(`${API}/categories`); setCategories(res.data); }
    catch (err) { console.error('Error fetching categories:', err); }
  };

  const fetchBrands = async () => {
    try { const res = await axios.get(`${API}/products/brands/list`); setBrands(res.data.filter(b => b && b.trim() !== '')); }
    catch (err) { console.error('Error fetching brands:', err); }
  };

  const fetchSuppliers = async () => {
    try { const res = await axios.get(`${API}/admin/suppliers`); setSuppliers(res.data.filter(s => s.is_active !== false)); }
    catch (err) { console.error('Error fetching suppliers:', err); }
  };

  const handleCreateBrand = () => {
    if (!newBrandName.trim()) { toast.error('Brand name is required'); return; }
    const trimmedBrand = newBrandName.trim();
    if (!brands.includes(trimmedBrand)) setBrands([...brands, trimmedBrand]);
    setForm({ ...form, brand: trimmedBrand });
    setNewBrandName('');
    setNewBrandDialogOpen(false);
    toast.success('Brand added successfully');
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) { toast.error('Category name is required'); return; }
    try {
      const slug = newCategorySlug.trim() || newCategoryName.toLowerCase().replace(/\s+/g, '-');
      const res = await axios.post(`${API}/admin/categories`, { name: newCategoryName.trim(), slug });
      await fetchCategories();
      setForm({ ...form, category_id: res.data.id });
      setNewCategoryName(''); setNewCategorySlug(''); setNewCategoryDialogOpen(false);
      toast.success('Category created successfully');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create category'); }
  };

  const handleUpdateCategory = async (catId, editingCat) => {
    try {
      await axios.put(`${API}/admin/categories/${catId}`, { name: editingCat.name, slug: editingCat.slug });
      toast.success('Category updated');
      fetchCategories();
    } catch { toast.error('Failed to update category'); }
  };

  const handleDeleteCategory = async (catId) => {
    if (!window.confirm('Delete this category? Products in this category will need reassignment.')) return;
    try { await axios.delete(`${API}/admin/categories/${catId}`); toast.success('Category deleted'); fetchCategories(); }
    catch { toast.error('Failed to delete category'); }
  };

  const handleRenameBrand = async (oldName, newName) => {
    try {
      const res = await axios.get(`${API}/admin/products?limit=100`);
      const productsWithBrand = (res.data.products || []).filter(p => p.brand === oldName);
      for (const p of productsWithBrand) { await axios.put(`${API}/admin/products/${p.id}`, { brand: newName }); }
      toast.success(`Renamed "${oldName}" to "${newName}" (${productsWithBrand.length} products updated)`);
      fetchBrands(); fetchProducts();
    } catch { toast.error('Failed to rename brand'); }
  };

  const handleDeleteBrand = async (brandName) => {
    const res = await axios.get(`${API}/admin/products?limit=100`);
    const count = (res.data.products || []).filter(p => p.brand === brandName).length;
    if (!window.confirm(`Delete brand "${brandName}"? ${count} product(s) will have their brand cleared.`)) return;
    try {
      const productsWithBrand = (res.data.products || []).filter(p => p.brand === brandName);
      for (const p of productsWithBrand) { await axios.put(`${API}/admin/products/${p.id}`, { brand: '' }); }
      toast.success(`Brand deleted, ${productsWithBrand.length} products updated`);
      fetchBrands(); fetchProducts();
    } catch { toast.error('Failed to delete brand'); }
  };

  const handleSave = async () => {
    try {
      if (editingProduct) { await axios.put(`${API}/admin/products/${editingProduct.id}`, form); toast.success('Product updated'); }
      else { await axios.post(`${API}/admin/products`, form); toast.success('Product created'); }
      setDialogOpen(false); setEditingProduct(null); setForm(emptyProduct); fetchProducts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save product'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try { await axios.delete(`${API}/admin/products/${id}`); toast.success('Product deleted'); fetchProducts(); }
    catch { toast.error('Failed to delete product'); }
  };

  const openEdit = (product) => { setEditingProduct(product); setForm(product); setDialogOpen(true); };

  const openHistory = async (product) => {
    setHistoryProduct(product); setHistoryDialogOpen(true); setHistoryLoading(true);
    try { const res = await axios.get(`${API}/admin/products/${product.id}/receiving-history`); setReceivingHistory(res.data.records || []); }
    catch { setReceivingHistory([]); toast.error('Failed to load receiving history'); }
    finally { setHistoryLoading(false); }
  };

  const requestStockUpdate = (productId) => {
    const newQty = editingStock[productId];
    if (newQty === undefined) return;
    const product = products.find(p => p.id === productId);
    setStockConfirm({
      productId,
      productName: product?.name || 'Unknown',
      oldQty: product?.stock_qty ?? 0,
      newQty,
    });
    setStockReason('');
  };

  const confirmStockUpdate = async () => {
    if (!stockConfirm) return;
    const { productId, newQty } = stockConfirm;
    try {
      await axios.put(`${API}/admin/inventory/${productId}?stock_qty=${newQty}&reason=${encodeURIComponent(stockReason || 'Manual adjustment')}`);
      toast.success('Stock updated'); fetchProducts();
      setEditingStock(prev => { const c = { ...prev }; delete c[productId]; return c; });
    } catch { toast.error('Failed to update stock'); }
    finally { setStockConfirm(null); setStockReason(''); }
  };

  const openCreate = () => { setEditingProduct(null); setForm(emptyProduct); setDialogOpen(true); };

  const lowStockProducts = products.filter(p => p.stock_qty <= (p.low_stock_threshold || 5));

  return (
    <AdminLayout>
      <div data-testid="admin-products">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-black text-white uppercase tracking-tight">Products</h1>
          <Button onClick={openCreate} className="bg-[#78BE20] text-black hover:bg-[#65a31b]" data-testid="add-product-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Product
          </Button>
        </div>

        {/* Product Form Dialog */}
        <ProductFormDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          editingProduct={editingProduct}
          form={form}
          setForm={setForm}
          onSave={handleSave}
          categories={categories}
          brands={brands}
          suppliers={suppliers}
          onNewBrand={() => setNewBrandDialogOpen(true)}
          onNewCategory={() => setNewCategoryDialogOpen(true)}
        />

        {/* Low Stock Alert */}
        {lowStockProducts.length > 0 && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-4 mb-6" data-testid="low-stock-alert">
            <div className="flex items-center mb-2">
              <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
              <h2 className="text-red-500 font-bold text-sm">Low Stock Alert</h2>
            </div>
            <div className="flex flex-wrap gap-2">
              {lowStockProducts.slice(0, 8).map(p => (
                <Badge key={p.id} className="bg-red-500/20 text-red-500 text-xs">{p.name} ({p.stock_qty})</Badge>
              ))}
              {lowStockProducts.length > 8 && (
                <Badge className="bg-red-500/20 text-red-500 text-xs">+{lowStockProducts.length - 8} more</Badge>
              )}
            </div>
          </div>
        )}

        {/* Categories & Brands Management */}
        <CategoryManager
          categories={categories}
          onUpdate={handleUpdateCategory}
          onDelete={handleDeleteCategory}
          onAdd={() => setNewCategoryDialogOpen(true)}
        />
        <BrandManager
          brands={brands}
          products={products}
          onRename={handleRenameBrand}
          onDelete={handleDeleteBrand}
          onAdd={() => setNewBrandDialogOpen(true)}
        />

        {/* Products Table */}
        <div className="bg-[#18181b] border border-zinc-800 rounded-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-zinc-900">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Product</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">SKU</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Price</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Stock</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-zinc-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}><td colSpan={6} className="px-4 py-4"><div className="h-8 bg-zinc-800 rounded animate-pulse" /></td></tr>
                ))
              ) : products.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-zinc-500">No products found</td></tr>
              ) : (
                products.map(product => (
                  <tr key={product.id} className="hover:bg-zinc-900/50">
                    <td className="px-4 py-4">
                      <div className="flex items-center">
                        {product.images?.[0] && <img src={product.images[0]} alt="" className="w-10 h-10 rounded object-cover mr-3" />}
                        <div>
                          <p className="text-white font-medium">{product.name}</p>
                          <p className="text-xs text-zinc-500">{product.brand}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-zinc-400 font-mono text-sm">{product.sku}</td>
                    <td className="px-4 py-4 text-[#78BE20] font-mono">${product.pricing?.retail?.toFixed(2)}</td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-1.5">
                        <Input
                          type="number"
                          value={editingStock[product.id] ?? product.stock_qty}
                          onChange={(e) => setEditingStock(prev => ({ ...prev, [product.id]: parseInt(e.target.value) || 0 }))}
                          onKeyDown={(e) => e.key === 'Enter' && requestStockUpdate(product.id)}
                          className={`w-20 h-8 text-center text-sm bg-zinc-800 border-zinc-700 ${product.stock_qty <= (product.low_stock_threshold || 5) ? 'text-red-500 border-red-500/30' : 'text-white'}`}
                          data-testid={`stock-input-${product.id}`}
                        />
                        {editingStock[product.id] !== undefined && editingStock[product.id] !== product.stock_qty && (
                          <Button size="sm" className="h-8 px-2 bg-[#78BE20] text-black hover:bg-[#65a31b] text-xs" onClick={() => requestStockUpdate(product.id)} data-testid={`stock-save-${product.id}`}>Save</Button>
                        )}
                        {product.track_serials && <Hash className="h-3 w-3 text-[#78BE20] flex-shrink-0" title="Serial tracked" />}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <Badge className={product.is_active ? 'bg-[#78BE20]/20 text-[#78BE20]' : 'bg-zinc-700 text-zinc-400'}>{product.is_active ? 'Active' : 'Inactive'}</Badge>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <Button variant="ghost" size="icon" onClick={() => openHistory(product)} className="text-zinc-400 hover:text-[#78BE20]" title="Receiving History"><History className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => openEdit(product)} className="text-zinc-400 hover:text-white"><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(product.id)} className="text-zinc-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <Button variant="outline" size="icon" disabled={pagination.page <= 1} onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))} className="border-zinc-700"><ChevronLeft className="h-4 w-4" /></Button>
            <span className="text-sm text-zinc-400 px-4">Page {pagination.page} of {pagination.pages}</span>
            <Button variant="outline" size="icon" disabled={pagination.page >= pagination.pages} onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))} className="border-zinc-700"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        )}
      </div>

      {/* Add New Brand Dialog */}
      <Dialog open={newBrandDialogOpen} onOpenChange={setNewBrandDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader><DialogTitle className="text-white">Add New Brand</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Brand Name</Label>
              <Input value={newBrandName} onChange={(e) => setNewBrandName(e.target.value)} placeholder="e.g., Samsung, Apple, Sony" className="mt-1 bg-zinc-800 border-zinc-700" data-testid="new-brand-input" onKeyDown={(e) => e.key === 'Enter' && handleCreateBrand()} />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setNewBrandDialogOpen(false)} className="flex-1 border-zinc-700">Cancel</Button>
              <Button onClick={handleCreateBrand} className="flex-1 bg-[#78BE20] text-black hover:bg-[#65a31b]" data-testid="save-new-brand-btn">Add Brand</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add New Category Dialog */}
      <Dialog open={newCategoryDialogOpen} onOpenChange={setNewCategoryDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader><DialogTitle className="text-white">Add New Category</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Category Name</Label>
              <Input value={newCategoryName} onChange={(e) => { setNewCategoryName(e.target.value); setNewCategorySlug(e.target.value.toLowerCase().replace(/\s+/g, '-')); }} placeholder="e.g., Network Switches" className="mt-1 bg-zinc-800 border-zinc-700" data-testid="new-category-name-input" />
            </div>
            <div>
              <Label>Slug (URL-friendly)</Label>
              <Input value={newCategorySlug} onChange={(e) => setNewCategorySlug(e.target.value)} placeholder="e.g., network-switches" className="mt-1 bg-zinc-800 border-zinc-700" data-testid="new-category-slug-input" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setNewCategoryDialogOpen(false)} className="flex-1 border-zinc-700">Cancel</Button>
              <Button onClick={handleCreateCategory} className="flex-1 bg-[#78BE20] text-black hover:bg-[#65a31b]" data-testid="save-new-category-btn">Create Category</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Receiving History Dialog */}
      <ReceivingHistoryDialog
        open={historyDialogOpen}
        onOpenChange={setHistoryDialogOpen}
        product={historyProduct}
        history={receivingHistory}
        loading={historyLoading}
      />

      {/* Stock Adjustment Warning Dialog */}
      <Dialog open={!!stockConfirm} onOpenChange={(open) => { if (!open) setStockConfirm(null); }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md" data-testid="stock-adjust-warning-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-400 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" /> Manual Stock Adjustment
            </DialogTitle>
            <DialogDescription className="text-zinc-400 pt-2">
              Stock changes should typically be made via <strong className="text-white">Purchase Orders</strong> to maintain accurate records. Only use manual adjustments for corrections, stocktakes, or write-offs.
            </DialogDescription>
          </DialogHeader>

          {stockConfirm && (
            <div className="space-y-4 mt-2">
              <div className="bg-zinc-800 rounded-sm p-3 space-y-1">
                <p className="text-sm text-zinc-400">Product</p>
                <p className="text-white font-medium">{stockConfirm.productName}</p>
                <div className="flex items-center gap-3 mt-2">
                  <span className="text-zinc-400 text-sm">Current: <strong className="text-white">{stockConfirm.oldQty}</strong></span>
                  <span className="text-zinc-500">&rarr;</span>
                  <span className="text-sm">New: <strong className="text-amber-400">{stockConfirm.newQty}</strong></span>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${stockConfirm.newQty > stockConfirm.oldQty ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                    {stockConfirm.newQty > stockConfirm.oldQty ? '+' : ''}{stockConfirm.newQty - stockConfirm.oldQty}
                  </span>
                </div>
              </div>

              <div>
                <Label className="text-zinc-400 text-sm">Reason for adjustment</Label>
                <TextareaInput
                  value={stockReason}
                  onChange={(e) => setStockReason(e.target.value)}
                  placeholder="e.g., Stocktake correction, damaged goods write-off..."
                  className="mt-1 bg-zinc-800 border-zinc-700 text-white text-sm"
                  rows={2}
                  data-testid="stock-adjust-reason"
                />
              </div>

              <DialogFooter className="gap-2 sm:gap-0">
                <Button variant="outline" onClick={() => setStockConfirm(null)} className="border-zinc-700" data-testid="stock-adjust-cancel">
                  Cancel
                </Button>
                <Button
                  onClick={confirmStockUpdate}
                  className="bg-amber-500 text-black hover:bg-amber-400 font-medium"
                  data-testid="stock-adjust-confirm"
                >
                  Confirm Adjustment
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
